import { useState, useEffect } from "react";
import { Building2, Plus, Trash2, Loader2, Users, Database } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";



const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  
  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`);
      setTenants(response.data);
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
      // May fail if no tenants exist yet - that's ok
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTenants();
  }, []);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/tenants`, formData);
      toast.success("Tenant created successfully");
      setFormData({ name: "", description: "" });
      setDialogOpen(false);
      fetchTenants();
    } catch (error) {
      console.error("Failed to create tenant:", error);
      toast.error(error.response?.data?.detail || "Failed to create tenant");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tenantId) => {
    try {
      await axios.delete(`${API}/tenants/${tenantId}`);
      toast.success("Tenant deleted");
      fetchTenants();
    } catch (error) {
      console.error("Failed to delete tenant:", error);
      toast.error(error.response?.data?.detail || "Failed to delete tenant");
    }
  };

  return (
    <Card className="card-industrial" data-testid="tenant-management">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organizations / Tenants
            </CardTitle>
            <CardDescription>Manage customer organizations for multi-tenancy</CardDescription>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-tenant-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-tenant-dialog">
              <DialogHeader>
                <DialogTitle>Create New Organization</DialogTitle>
                <DialogDescription>Add a new customer organization</DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Organization Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Acme Corporation"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="input-tenant-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Brief description of the organization"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="input-tenant-description"
                  />
                </div>
                
                <DialogFooter className="mt-6">
                  <DialogClose asChild>
                    <Button type="button" variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={submitting} data-testid="submit-tenant">
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Organization
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-muted-foreground mb-2">No organizations yet</p>
            <p className="text-xs text-muted-foreground">
              Create organizations to isolate SAP connections per customer
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="tenants-list">
            {tenants.map((tenant) => (
              <div
                key={tenant.id}
                className={`flex items-center justify-between p-3 rounded-md border ${
                  !tenant.is_active ? 'bg-secondary/50 opacity-60' : 'bg-background'
                }`}
                data-testid={`tenant-row-${tenant.name}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {tenant.name}
                      {tenant.is_active && (
                        <Badge variant="outline" className="text-xs">Active</Badge>
                      )}
                    </p>
                    {tenant.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {tenant.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Created: {new Date(tenant.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(tenant.id, tenant.name)}
                    data-testid={`delete-tenant-${tenant.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TenantManagement;
